import React from 'react'
import PropTypes from 'prop-types'
import {
    Flex,
    Skeleton,
    Box,
    Text,
    Fade,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import Swatch from '../../swatch-group/swatch'
import SwatchGroup from '../../swatch-group'
import {FewItemsLeftIcon, FewItemsLeftMessage} from '../../few-items-left'
import {NotifyMeEnvelope} from '../../icons/index'
import ActualFitMessage from '../../actual-fit-message'
import loadable from '@loadable/component'
import {useIntl} from 'react-intl'
import ErrorMessage from '../../error-message'

const SizeChartDrawer = loadable(() => import('../../size-chart-drawer'), {ssr: false})

const VariationSelector = ({
    variationAttributes,
    showLoading,
    isGiftPage,
    showOptionsMessage,
    missingAttributeMessages,
    customInventoryMessage,
    product,
    notifyMeEnabled,
    variationParams,
    optimizelyContent,
    sizeChartId,
    category,
    onReviewsClick,
    fitValue,
    hasBazaarVoiceReviews,
    isFewItemsLeft,
    isNotifyMe,
    currency
}) => {
    const styles = useMultiStyleConfig('ProductView')
    const intl = useIntl()
    const {currency: activeCurrency} = useCurrency()
    const currencyCode = currency || product?.currency || activeCurrency

    const notifyMeIcon = (value, orderable) => {
        const hasSizeVariation = product?.variationAttributes?.find((attr) => attr.id === 'size')
        const hasLengthVariation = product?.variationAttributes?.find(
            (attr) => attr.id === 'length'
        )
        let show = false

        if (hasSizeVariation && !hasLengthVariation) {
            const notifyMeVariations = product?.c_notifyMeArray?.find(({size}) => size === value)
            show = notifyMeEnabled && notifyMeVariations?.size === value && !orderable
        }

        if (hasSizeVariation && hasLengthVariation) {
            const selectedLength = variationParams?.length
            if (selectedLength) {
                const notifyMeVariations = product?.c_notifyMeArray?.find(
                    ({size, length}) => size === value && length === selectedLength
                )
                show =
                    notifyMeEnabled &&
                    notifyMeVariations?.size === value &&
                    notifyMeVariations?.length === selectedLength &&
                    !orderable
            }
        }

        return show
    }

    if (showLoading) {
        return (
            <>
                <Skeleton height={6} width={32} />
                <Skeleton height={20} width={64} />
                <Skeleton height={6} width={32} />
                <Skeleton height={20} width={64} />
            </>
        )
    }

    const sizeAttribute = (variationAttributes || []).find(({id}) => id === 'size')
    const selectedSizeOrderable = sizeAttribute?.values?.find(
        ({value}) => value === sizeAttribute?.selectedValue?.value
    )?.orderable

    return (variationAttributes || []).map(
        ({id, name, selectedValue, values}, subheadingIndex) => {
            const showAttributeError =
                !showLoading && showOptionsMessage && missingAttributeMessages[id]
            const showSizeHeaderError = id === 'size' && showAttributeError
            const showLengthHeaderError = id === 'length' && showAttributeError
            const showPriceValueHeaderError = id === 'priceValue' && showAttributeError
            // Determine if this is an E-Gift Card based on selected cardType
            // This ensures instant visual transition when switching card types
            const selectedCardTypeFromAttributes = variationAttributes?.find(
                (attr) => attr.id === 'cardType'
            )?.selectedValue?.value
            const selectedCardType =
                selectedCardTypeFromAttributes || variationParams?.cardType
            const normalizedCardType = selectedCardType?.toString().toUpperCase()
            const isEGiftCardSelected =
                normalizedCardType === 'EGC' ||
                (!selectedCardType && product.c_isEGiftCard)

            const swatches = values.map(
                ({href, name, image, value, orderable}, index) => {
                    const availabilityTestId = `${id}-${orderable ? 'available' : 'unavailable'}`
                    const isSelected = selectedValue?.value === value
                    const parsedValue = Number(value)
                    const isValidPriceAmount = Number.isFinite(parsedValue)
                    const displaySwatchName =
                        id === 'priceValue' && isGiftPage && isValidPriceAmount && currencyCode
                            ? intl.formatNumber(parsedValue, {
                                  style: 'currency',
                                  currency: currencyCode,
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0
                              })
                            : name
                    const content = image ? (
                        <Box
                            {...styles.swatchImageBox}
                            borderRadius={isEGiftCardSelected ? 3 : 0}
                            backgroundColor={name.toLowerCase()}
                            backgroundImage={`url(${image.disBaseLink || image.link})`}
                            data-testid={availabilityTestId}
                        />
                    ) : (
                        <Box
                            as="span"
                            {...styles.swatchTextBox}
                            className="swatch-content"
                            data-testid={availabilityTestId}
                            color={isSelected ? 'webTypo.textOnColor' : 'webTypo.textPrimary'}
                        >
                            {displaySwatchName}
                        </Box>
                    )
                    const hasSelection = Boolean(selectedValue?.value)
                    const isFirst = index === 0
                    // To mimic the behavior of a native radio input, only
                    // one swatch should receive tab focus; the rest can be
                    // selected using arrow keys when the swatch group has
                    // focus. The focused element is the selected option or
                    // the first in the group, if no option is selected.
                    // This is a slight difference, for simplicity, from the
                    // native element, where the first element is focused on
                    // `Tab` and the _last_ element is focused on `Shift+Tab`
                    const isFocusable =
                        isSelected || (!hasSelection && isFirst)

                    const showNotifyMeIcon = notifyMeIcon(
                        value,
                        orderable
                    )
                    const hasLengthVariation = product?.variationAttributes?.find(
                        (attr) => attr.id === 'length'
                    )
                    const selectedLength = variationParams?.length
                    const fewItemsLeft =
                        hasLengthVariation && selectedLength
                            ? product?.c_fewItemsLeftArray?.find(
                                  ({size, length}) => size === value && length === selectedLength
                              )
                            : product?.c_fewItemsLeftArray?.find(
                                  ({size}) => size === value
                              )

                    return (
                        <Swatch
                            isPDP
                            key={value}
                            href={href}
                            disabled={!orderable}
                            isNotifyMe={showNotifyMeIcon}
                            value={value}
                            name={name}
                            variant={
                                id === 'color'
                                    ? isEGiftCardSelected
                                        ? 'giftCard'
                                        : 'circle'
                                    : 'square'
                            }
                            selected={isSelected}
                            isFocusable={isFocusable}
                            id={id}
                            isFirstColorSwatch={id === 'color' && index === 0}
                            isGiftPage={isGiftPage}
                        >
                            {showNotifyMeIcon && (
                                <NotifyMeEnvelope {...styles.notifyMeEnvelope} />
                            )}
                            {!showNotifyMeIcon &&
                                orderable &&
                                fewItemsLeft && (
                                    <FewItemsLeftIcon
                                        position="absolute"
                                        top="1"
                                        right="1"
                                    />
                                )}
                            {content}
                        </Swatch>
                    )
                }
            )
            const isLastAttribute =
                subheadingIndex === variationAttributes.length - 1
            return (
                <React.Fragment key={id}>
                    {id === 'size' && (
                        <Flex {...styles.sizeHeaderFlex}>
                            {showSizeHeaderError ? (
                                <ErrorMessage containerProps={styles.errorText}
                                    message={missingAttributeMessages[id]}
                                />
                            ) : (
                                <Text {...styles.sizeLabel}>
                                    {intl.formatMessage({
                                        defaultMessage: 'Size',
                                        id: 'product_list.button.size'
                                    })}
                                </Text>
                            )}

                            {!isNotifyMe && isFewItemsLeft && selectedSizeOrderable && (
                                <FewItemsLeftMessage data-testid="pdp-few-items-left" containerProps={{ marginTop: 0, paddingLeft: 1}}/>
                            )}
                            <SizeChartDrawer
                                optimizelyContent={optimizelyContent}
                                sizeChartId={sizeChartId}
                            />
                        </Flex>
                    )}
                    <Box mt={id === 'length' && 6}>
                        {showLengthHeaderError && (
                            <ErrorMessage
                                containerProps={styles.errorText}
                                message={missingAttributeMessages[id]}
                            />
                        )}
                        {showPriceValueHeaderError && (
                            <ErrorMessage
                                containerProps={styles.errorText}
                                message={missingAttributeMessages[id]}
                            />
                        )}
                        {/* Swatch group */}
                        <SwatchGroup
                            isPDP
                            isSizeSwatch={id !== 'color'}
                            giftCardType={id === 'cardType'}
                            giftCardValue={id === 'priceValue'}
                            giftECard={
                                id === 'color' && isEGiftCardSelected
                            }
                            isGiftPage={isGiftPage}
                            value={selectedValue?.value}
                            displayValueOnly={
                                isGiftPage
                                    ? false
                                    : ['color', 'size'].includes(id)
                            }
                            displayLabelOnly={isGiftPage ? true : false}
                            displayName={
                                selectedValue?.name ||
                                selectedValue?.name ||
                                ''
                            }
                            label={(showLengthHeaderError || showPriceValueHeaderError) ? '' : intl.formatMessage(
                                {
                                    defaultMessage: '{variantType}',
                                    id: 'product_view.label.variant_type'
                                },
                                {
                                variantType: isGiftPage
                                    ? (id === 'color' && selectedValue?.name ? selectedValue.name : name)
                                    : name
                                }
                            )}
                            id={id}
                        >
                            {swatches}
                        </SwatchGroup>
                        {!showLoading &&
                            showOptionsMessage &&
                            missingAttributeMessages[id] &&
                            id !== 'size' &&
                            id !== 'length' &&
                            id !== 'priceValue' && (
                                <Fade in={true}>
                                    <ErrorMessage
                                        containerProps={styles.errorText}
                                        message={missingAttributeMessages[id]}
                                    />
                                </Fade>
                            )}
                        {isLastAttribute &&
                            !showLoading &&
                            customInventoryMessage && (
                                <Fade in={true}>
                                    <ErrorMessage
                                        containerProps={styles.errorText}
                                        message={customInventoryMessage}
                                    />
                                </Fade>
                            )}
                    </Box>
                    {id === 'size' && fitValue && hasBazaarVoiceReviews && (
                        <Box mt="2">
                            <ActualFitMessage
                                fit={fitValue}
                                onClick={onReviewsClick}
                                textProps={{ 
                                    color: "webTypo.textSecondary"
                                }}
                            />
                        </Box>
                    )}
                </React.Fragment>
            )
        }
    )
}

VariationSelector.propTypes = {
    variationAttributes: PropTypes.array,
    showLoading: PropTypes.bool,
    isGiftPage: PropTypes.bool,
    showOptionsMessage: PropTypes.bool,
    missingAttributeMessages: PropTypes.object,
    customInventoryMessage: PropTypes.string,
    product: PropTypes.object,
    notifyMeEnabled: PropTypes.bool,
    variationParams: PropTypes.object,
    optimizelyContent: PropTypes.object,
    sizeChartId: PropTypes.string,
    category: PropTypes.array,
    onReviewsClick: PropTypes.func,
    fitValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    hasBazaarVoiceReviews: PropTypes.bool,
    isFewItemsLeft: PropTypes.bool,
    isNotifyMe: PropTypes.bool,
    currency: PropTypes.string
}

export default VariationSelector
